<template>
  <div v-if="show" class="mention-dropdown" :style="positionStyle">
    <div
      v-for="(user, index) in displayedUsers"
      :key="user.id"
      :class="['mention-dropdown__item', { 'mention-dropdown__item--selected': index === selectedIndex }]"
      @click="selectUser(user)"
      @mouseenter="selectedIndex = index"
      data-test-id="mention-user-option"
    >
      {{ user.fullName }}
    </div>
    <div v-if="users.length === 0" class="mention-dropdown__item mention-dropdown__item--empty">
      {{ t('chat.conversation.noUsersFound') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { TaggableUserType } from '@/features/chat/types'
import { useI18n } from '@/lib/i18n'

const { t } = useI18n()

const props = defineProps<{
  show: boolean
  users: TaggableUserType[]
  position?: { top: number; left: number }
}>()

const emit = defineEmits<{
  'select-user': [user: TaggableUserType]
}>()

const selectedIndex = ref(0)

// Max de 3 pour ne pas surcharger
const displayedUsers = computed(() => {
  return props.users.slice(0, 3)
})

const positionStyle = computed(() => {
  if (props.position) {
    return {
      bottom: `${props.position.bottom}px`,
      left: `${props.position.left}px`,
      transform: 'translateY(-100%)',
    }
  }
  return {}
})

watch(
  () => props.users,
  () => {
    selectedIndex.value = 0
  },
)

function selectUser(user: TaggableUserType) {
  emit('select-user', user)
}

defineExpose({
  selectNext() {
    if (selectedIndex.value < displayedUsers.value.length - 1) {
      selectedIndex.value++
    }
  },
  selectPrevious() {
    if (selectedIndex.value > 0) {
      selectedIndex.value--
    }
  },
  selectCurrent() {
    if (displayedUsers.value[selectedIndex.value]) {
      selectUser(displayedUsers.value[selectedIndex.value])
    }
  },
})
</script>

<style lang="scss" scoped>
@import '@/styles/utils';

.mention-dropdown {
  position: absolute;
  background-color: rgb($white);
  border: 1px solid rgba($black, $opacity-10);
  border-radius: $s * 2;
  box-shadow: 0 -4px 12px rgba($black, $opacity-10);
  z-index: z('mention-dropdown');
  min-width: 200px;
  max-width: 300px;
  overflow: hidden; 

  &__item {
    padding: $s * 2 $s * 3;
    cursor: pointer;
    transition: background-color 0.2s;
    position: relative;
    width: 100%;
    box-sizing: border-box;

    &:hover,
    &--selected {
      background-color: rgba($primary, $opacity-10);
    }

    &:first-child {
      &:hover,
      &--selected {
        border-top-left-radius: $s * 2;
        border-top-right-radius: $s * 2;
      }
    }

    &:last-child {
      &:hover,
      &--selected {
        border-bottom-left-radius: $s * 2;
        border-bottom-right-radius: $s * 2;
      }
    }

    &:only-child {
      &:hover,
      &--selected {
        border-radius: $s * 2;
      }
    }

    &--empty {
      color: rgb($text-muted);
      cursor: default;

      &:hover {
        background-color: transparent;
      }
    }
  }
}
</style>

